import { Anchor, Box, Image, Stack, Text, Title, getModuleUrl, useReducedMotion, useTheme } from "@dehub/theme";
import {
  ArrowsLeftRightIcon,
  CheckIcon,
  CubeIcon,
  PackageIcon,
  ShoppingCartIcon,
} from "@phosphor-icons/react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import authSideVideo from "@/assets/auth-side.mp4";
import classes from "./RegisterShell.module.css";

function SideCard({ icon, title, items }: { icon: ReactNode; title: string; items: string[] }) {
  return (
    <Box className={classes.side}>
      <Text span className={classes.sideTitle}>
        {icon}
        {title}
      </Text>
      <Box component="ul" className={classes.sideList}>
        {items.map((item) => (
          <Box component="li" key={item} className={classes.sideItem}>
            <Box component="span" className={classes.bullet} aria-hidden="true" />
            {item}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** de-auth-admin AuthShell-ийн платформын самбар (canvas 1.1) — нэвтрэх, бүртгүүлэх хоёр ижил. */
function PlatformPanel() {
  const { t } = useTranslation("register");
  const benefits = [t("shell.benefit1"), t("shell.benefit2"), t("shell.benefit3")];

  return (
    <Box component="section" aria-label={t("shell.panelLabel")} className={classes.panel}>
      <Stack gap={12}>
        <Text className={classes.headline}>{t("shell.headline")}</Text>
        <Text className={classes.panelLead}>{t("shell.lead")}</Text>
      </Stack>

      <Box component="ul" className={classes.benefits}>
        {benefits.map((text) => (
          <Box component="li" key={text} className={classes.benefit}>
            <CheckIcon size={18} weight="bold" className={classes.benefitIcon} aria-hidden="true" />
            {text}
          </Box>
        ))}
      </Box>

      <Box className={classes.diagram}>
        <SideCard
          icon={<CubeIcon size={18} aria-hidden="true" />}
          title={t("shell.supplier")}
          items={[t("shell.supplierItem1"), t("shell.supplierItem2"), t("shell.supplierItem3")]}
        />
        <Box className={classes.link} aria-hidden="true">
          <ArrowsLeftRightIcon size={22} />
          <Box component="span" className={classes.hub}>
            <PackageIcon size={22} />
          </Box>
        </Box>
        <SideCard
          icon={<ShoppingCartIcon size={18} aria-hidden="true" />}
          title={t("shell.buyer")}
          items={[t("shell.buyerItem1"), t("shell.buyerItem2"), t("shell.buyerItem3")]}
        />
      </Box>
    </Box>
  );
}

/**
 * NEXT tenant-ийн зүүн самбар: de-auth-admin нэвтрэх хуудастай ижил агуулахын видео
 * (дуугүй, давталттай, чимэглэл). prefers-reduced-motion үед автоматаар тоглуулахгүй.
 */
function VideoPanel() {
  const { t } = useTranslation("register");
  const reduceMotion = useReducedMotion();

  return (
    <Box component="section" aria-label={t("shell.panelLabel")} className={classes.videoPanel}>
      <video
        className={classes.video}
        src={authSideVideo}
        autoPlay={!reduceMotion}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
      />
    </Box>
  );
}

/** Tenant-ийн лого; лого алга бол нэрийн primary пилл. */
function TenantMark() {
  const { tenant } = useTheme();
  const name = tenant?.name?.trim() ?? "";

  if (tenant?.logo) {
    return <Image src={tenant.logo} h={40} w="auto" maw="100%" fit="contain" draggable={false} alt={name} className={classes.logo} />;
  }
  if (!name) return null;
  return (
    <Text span translate="no" className={classes.pill}>
      {name}
    </Text>
  );
}

/**
 * Бүртгүүлэх хуудасны хүрээ: de-auth-admin нэвтрэх хуудасны (canvas 1.1) загвар —
 * саарал дэвсгэр дээр төвд карт, зүүн талд NEXT-д видео / бусад tenant-д платформын бараан самбар, баруун талд маягт.
 * Маягт хоёр баганатай тул карт 1200 px.
 */
export function RegisterShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation("register");
  const { tenant, tokens } = useTheme();
  const tenantName = tenant?.name?.trim() ?? "";
  // Видеон дээр NEXT-ийн лого байгаа тул зөвхөн NEXT tenant-д (de-auth-admin AuthShell-тэй ижил шалгуур).
  const isNext = tenantName.toLowerCase() === "next";
  const loginUrl = `${getModuleUrl("", tenant.modulesDomain)}/login`;

  const vars = { "--panel-bg": tokens.navbar?.bg } as CSSProperties;

  return (
    <Box className={classes.page} style={vars}>
      <Anchor href="#main" className={classes.skip}>
        {t("shell.skip")}
      </Anchor>

      <Box className={classes.center}>
        <Box className={classes.card}>
          {isNext ? <VideoPanel /> : <PlatformPanel />}
          <Box component="main" id="main" tabIndex={-1} className={classes.main}>
            {isNext ? (
              <Box className={classes.brand}>
                <TenantMark />
                <Box component="span" className={classes.brandDivider} aria-hidden="true" />
                <Text span className={classes.brandTagline}>
                  {t("shell.tagline")}
                </Text>
              </Box>
            ) : (
              <TenantMark />
            )}
            <Stack gap={6}>
              <Title order={1} fz={32} fw={700} lts="-0.03em" c="var(--mantine-color-text)">
                {t("shell.title")}
              </Title>
              <Text fz={15} c="gray.7" lh={1.5}>
                {t("shell.haveAccount")}{" "}
                <Anchor href={loginUrl} className={classes.accentLink}>
                  {t("shell.login")}
                </Anchor>
              </Text>
            </Stack>
            {children}
          </Box>
        </Box>
      </Box>

      <Box component="footer" className={classes.footer}>
        <Text span inherit>
          © {new Date().getFullYear()} {tenantName}
        </Text>
      </Box>
    </Box>
  );
}
